import ActionIconButton from '@components/Common/ActionIconButton';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import ErrorMessage from '@components/Common/ErrorMessage';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { canWriteJobsAndCompanies, isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import { formatDateTime } from '@helpers/date';
import { humanizeEnum } from '@helpers/labels';
import { lastListView } from '@helpers/listView';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import { Box, Button, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { requestTotal, runCounts, runError, scoutLine, usd, type Alert, type Run } from './index';
import SourceJobsTable from './SourceJobsTable';
import {
  API,
  canApproveSource,
  approveHiddenReason,
  canRunSource,
  canScoutSource,
  errorMessage,
  useSourceActions,
} from './sourceActions';
import { nextFetchState, paramLines, refreshLabel, robotsLabel, sourceEvents, type SourceDetail } from './sourceDetail';
import { jobsCount } from './sourceJobs';
import './index.scss';

/**
 * One external jobs source, as an entity workspace (owner, staging review: the jobs drawer "should
 * show the status too and everything in detail").
 *
 * It replaces SourceJobsDrawer and carries everything that did: the fetched jobs with their links,
 * pay and enrichment, Approve and Decline for a Super Admin while the source is in review, and Run
 * now when nothing has been fetched. It adds what the drawer could not hold: the source's whole
 * record (status, approval, scheduling, robots, the Scout's findings, adapter settings), its run
 * history with errors, its open alerts, and the row actions (Run, Scout, Pause, Take down), which
 * share one implementation with the list through useSourceActions.
 */

const TABS: WorkspaceTab[] = [
  { id: 'overview', label: 'Overview', icon: <InfoOutlinedIcon /> },
  { id: 'jobs', label: 'Fetched jobs', icon: <ListAltOutlinedIcon /> },
  { id: 'runs', label: 'Runs', icon: <HistoryIcon /> },
];

const RUN_LIMIT = 50;

const initialsOf = (name: string) => {
  const words = name.trim().split(/[\s_-]+/).filter(Boolean);
  return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : (words[0] ?? '').slice(0, 2)).toUpperCase() || 'S';
};

const yesNo = (v: boolean | null | undefined) => (v === true ? 'Yes' : v === false ? 'No' : null);

const SourceWorkspace: React.FC = () => {
  const { sourceId = '' } = useParams<{ sourceId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const role = useCurrentUserRole();
  const superAdmin = isSuperAdmin(role);
  // Marketing reads a source but does not run or scout it (owner's brief, 2026-10-19).
  const canOperate = canWriteJobsAndCompanies(role);
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'overview');
  const [activityOpen, toggleActivity] = useActivityRail();

  const [source, setSource] = useState<SourceDetail | null>(null);
  const [liveJobs, setLiveJobs] = useState<number | null>(null);
  const [fetchedTotal, setFetchedTotal] = useState<number | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [runsFailed, setRunsFailed] = useState(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* Which source the latest load is for. Moving between sources quickly let an earlier, slower
     response land last and show the previous source's data under the new one's URL. */
  const loadFor = useRef(sourceId);
  const load = useCallback(() => {
    loadFor.current = sourceId;
    const stale = () => loadFor.current !== sourceId;
    setLoading(true);
    setError(null);
    const id = encodeURIComponent(sourceId);
    /* The single read is the source document. The two counts come from the jobs route: ACTIVE is
       what the list calls live jobs (its activeJobs), and no status is every job not yet closed. */
    const count = (params: Record<string, unknown>) =>
      axios
        .get(`${API}/sources/${id}/jobs`, { params: { ...params, page: 0, size: 1 } })
        .then((r) => (typeof r.data?.totalElements === 'number' ? r.data.totalElements : null))
        .catch(() => null);
    Promise.all([axios.get(`${API}/sources/${id}`), count({ status: 'ACTIVE' }), count({})])
      .then(([s, live, fetched]) => {
        if (stale()) return;
        setSource(s.data && typeof s.data === 'object' && s.data.sourceId ? s.data : null);
        if (!s.data?.sourceId) setError('This source could not be found.');
        setLiveJobs(live);
        setFetchedTotal(fetched);
      })
      .catch((e) =>
        stale() ? undefined : setError(e?.response?.status === 404 ? 'This source could not be found.' : errorMessage(e, 'The source could not be loaded.')),
      )
      .finally(() => { if (!stale()) setLoading(false); });
    // Runs and alerts are their own reads: a failure there should not take the record down.
    axios
      .get(`${API}/runs`, { params: { sourceId, limit: RUN_LIMIT } })
      .then((r) => {
        if (stale()) return;
        setRuns(Array.isArray(r.data) ? r.data : []);
        setRunsFailed(false);
      })
      .catch(() => setRunsFailed(true));
    axios
      .get(`${API}/alerts`)
      .then((r) => !stale() && setAlerts((Array.isArray(r.data) ? r.data : []).filter((a: Alert) => a.context?.sourceId === sourceId)))
      .catch(() => setAlerts([]));
  }, [sourceId]);

  useEffect(load, [load]);
  const actions = useSourceActions(load);

  const acknowledge = async (a: Alert) => {
    try {
      await axios.post(`${API}/alerts/${a.alertId}/ack`, {});
      setAlerts((list) => list.filter((x) => x.alertId !== a.alertId));
      showToast('Alert acknowledged', 'success');
    } catch (e) {
      showToast(errorMessage(e, 'The alert was not acknowledged.'), 'error');
    }
  };

  if (loading && !source) return <SkeletonLoader variant="workspace" />;
  if (!source) return <ErrorMessage page message={error ?? 'This source could not be found.'} onRetry={load} />;

  const name = source.companyName || source.sourceId;
  // The actions read activeJobs (the takedown dialog counts them), which the single read lacks.
  const actionable = { ...source, activeJobs: liveJobs ?? undefined };
  const inReview = source.status === 'NEEDS_REVIEW';
  const canApprove = canApproveSource(source);
  const canRun = canOperate && canRunSource(source);
  const next = nextFetchState(source);
  const nextText = next.kind === 'date' ? formatDateTime(next.at) : next.text;
  const scout = source.scout;
  const params = paramLines(source.params);

  const careersLink = source.careersUrl ? (
    <a className="cc-link" href={source.careersUrl} target="_blank" rel="noopener noreferrer">
      {source.careersUrl}
    </a>
  ) : null;

  const glance: GlanceRow[] = [
    { key: 'Live jobs', value: liveJobs === null ? '-' : liveJobs.toLocaleString('en-IN'), mono: true },
    { key: 'Last fetch found', value: typeof source.lastJobCount === 'number' ? source.lastJobCount.toLocaleString('en-IN') : '-', mono: true },
    { key: 'Last fetch', value: source.lastRunAt ? formatDateTime(source.lastRunAt) : 'Never run', mono: !!source.lastRunAt },
    { key: 'Next fetch', value: nextText, mono: next.kind === 'date' },
    {
      key: 'Approved',
      value: source.approvedAt ? `${formatDateTime(source.approvedAt)}${source.approvedBy ? `, ${source.approvedBy}` : ''}` : 'Not yet',
    },
  ];

  const railActions = (
    <>
      {superAdmin && canApprove && !inReview && (
        <ActionIconButton
          label="Approve: show its jobs on the site"
          color="primary"
          icon={<CheckCircleOutlinedIcon />}
          onClick={() => actions.approve(actionable)}
        />
      )}
      {canRun && <ActionIconButton label="Run now" icon={<PlayArrowOutlinedIcon />} onClick={() => actions.queueRun(actionable)} />}
      {canOperate && canScoutSource(source) && (
        <ActionIconButton label="Scout the site again" icon={<TravelExploreOutlinedIcon />} onClick={() => actions.scoutAgain(actionable)} />
      )}
      {superAdmin && source.status === 'ACTIVE' && (
        <ActionIconButton label="Pause daily runs" icon={<PauseCircleOutlinedIcon />} onClick={() => actions.pause(actionable)} />
      )}
      {superAdmin && source.status !== 'DECLINED' && (
        <ActionIconButton
          label="Take down: remove its jobs now"
          color="error"
          icon={<DeleteForeverOutlinedIcon />}
          onClick={() => actions.takedown(actionable)}
        />
      )}
    </>
  );

  const identityRail = (
    <IdentityRail
      initials={initialsOf(name)}
      name={name}
      subId={source.sourceId}
      statusChip={<CRMStatusChip status={source.status} />}
      badges={source.lastRunStatus ? <CRMStatusChip status={source.lastRunStatus} label={`Last run: ${humanizeEnum(source.lastRunStatus).toLowerCase()}`} /> : undefined}
      secondaryActions={railActions}
      glance={glance}
      audit={{
        createdAt: source.createdAt ?? undefined,
        createdBy: source.createdBy ?? undefined,
        updatedAt: source.statusChangedAt ?? undefined,
        updatedBy: source.statusChangedBy ?? undefined,
      }}
    />
  );

  /* A source in review is waiting on a decision, so the decision sits above everything else, as
     the drawer's Approve and Decline did. HR sees why it is waiting but not the buttons. */
  const decisionBar = inReview ? (
    <SectionCard title="Source review" icon={<FactCheckOutlinedIcon />} meta="Decision needed">
      <Box className="ej-decision">
        <Typography variant="body2" className="ej-decision-text">
          These jobs are hidden from candidates until the source is approved.
          {scoutLine(source) ? ` ${scoutLine(source)}.` : ''}
          {source.reviewReason ? ` Why it is in review: ${source.reviewReason}` : ''}
        </Typography>
        {superAdmin && approveHiddenReason(source) && (
          <Typography variant="body2" className="ej-decision-text">
            {approveHiddenReason(source)}
          </Typography>
        )}
        {superAdmin && (
          <Box className="ej-decision-actions">
            {canApprove && (
              <Button variant="contained" onClick={() => actions.approve(actionable)}>
                Approve
              </Button>
            )}
            <Button variant="outlined" color="error" onClick={() => actions.decline(actionable)}>
              Decline
            </Button>
          </Box>
        )}
      </Box>
    </SectionCard>
  ) : undefined;

  const overview = (
    <>
      <SectionCard title="Status and approval" icon={<FactCheckOutlinedIcon />}>
        <WorkspaceField label="Status" value={<CRMStatusChip status={source.status} />} />
        <WorkspaceField label="Approved" value={source.approvedAt ? formatDateTime(source.approvedAt) : 'Not yet'} />
        <WorkspaceField label="Approved by" value={source.approvedBy} />
        <WorkspaceField
          label="Last status change"
          value={source.statusChangedAt ? `${formatDateTime(source.statusChangedAt)}${source.statusChangedBy ? `, ${source.statusChangedBy}` : ''}` : null}
        />
        <WorkspaceField label="Why it is in review" full value={source.reviewReason} />
        <WorkspaceField label="Pause or decline reason" full value={source.declineReason} />
        {source.takedown?.at && (
          <WorkspaceField
            label="Taken down"
            full
            value={`${formatDateTime(source.takedown.at)}${source.takedown.by ? ` by ${source.takedown.by}` : ''}${source.takedown.reason ? `: ${source.takedown.reason}` : ''}${source.takedown.purged ? '. Its jobs were deleted.' : ''}`}
          />
        )}
      </SectionCard>

      <SectionCard title="Fetching" icon={<ScheduleOutlinedIcon />}>
        <WorkspaceField label="Live jobs" mono value={liveJobs === null ? null : liveJobs.toLocaleString('en-IN')} />
        <WorkspaceField label="Fetched, not closed" mono value={fetchedTotal === null ? null : fetchedTotal.toLocaleString('en-IN')} />
        <WorkspaceField label="Last fetch found" mono value={typeof source.lastJobCount === 'number' ? source.lastJobCount.toLocaleString('en-IN') : null} />
        <WorkspaceField label="Schedule" value={refreshLabel(source.refreshIntervalHours)} />
        <WorkspaceField label="Last fetch" value={source.lastRunAt ? formatDateTime(source.lastRunAt) : 'Never run'} />
        <WorkspaceField label="Last fetch result" value={source.lastRunStatus ? <CRMStatusChip status={source.lastRunStatus} /> : null} />
        <WorkspaceField label="Last good fetch" value={source.lastSuccessAt ? formatDateTime(source.lastSuccessAt) : null} />
        <WorkspaceField label="Next fetch" value={nextText} />
        <WorkspaceField
          label="Failures in a row"
          mono
          value={typeof source.consecutiveFailures === 'number' ? String(source.consecutiveFailures) : null}
          valueColor={(source.consecutiveFailures ?? 0) > 0 ? 'var(--red)' : undefined}
        />
        <WorkspaceField label="robots.txt" value={robotsLabel(source.robotsAllowed)} />
        <WorkspaceField label="robots.txt checked" value={source.robotsCheckedAt ? formatDateTime(source.robotsCheckedAt) : null} />
      </SectionCard>

      {alerts.length > 0 && (
        <SectionCard title="Open alerts" icon={<NotificationsActiveOutlinedIcon />} meta={String(alerts.length)}>
          <Box className="ej-alert-list">
            {alerts.map((a) => (
              <Box key={a.alertId} className="ej-alert">
                <Box className="ej-source">
                  <span className="ej-wrap">{a.message}</span>
                  <span className="ej-mono">
                    {humanizeEnum(a.kind)}
                    {a.createdAt ? `, ${formatDateTime(a.createdAt)}` : ''}
                  </span>
                </Box>
                <ActionIconButton size="small" label="Acknowledge" icon={<DoneAllOutlinedIcon />} onClick={() => acknowledge(a)} />
              </Box>
            ))}
          </Box>
        </SectionCard>
      )}

      <SectionCard title="Source details" icon={<SettingsOutlinedIcon />}>
        <WorkspaceField label="Company" value={source.companyName} />
        <WorkspaceField label="Source ID" mono value={source.sourceId} />
        <WorkspaceField label="Careers site" full value={careersLink} />
        <WorkspaceField label="Brands" value={(source.brandNames ?? []).filter(Boolean).join(', ')} />
        <WorkspaceField label="Adapter" mono value={source.adapter} />
        <WorkspaceField label="Fallback adapter" mono value={source.fallbackAdapter} />
        <WorkspaceField label="AI fallback allowed" value={yesNo(source.llmFallbackAllowed)} />
        <WorkspaceField label="Job ids" value={source.jobCodeKind === 'hash' ? 'Content hash (no stable id)' : source.jobCodeKind ? 'From the careers site' : null} />
        <WorkspaceField label="Countries" value={(source.countryPolicy?.allowed ?? []).join(', ')} />
        <WorkspaceField label="Remote jobs" value={source.countryPolicy ? (source.countryPolicy.remote === false ? 'Left out' : 'Included') : null} />
        <WorkspaceField label="Legal status" value={source.legalStatus ? humanizeEnum(source.legalStatus) : null} />
        <WorkspaceField label="Terms" full value={source.termsNote} />
        <WorkspaceField label="Notes" full value={source.notes} />
        <WorkspaceField
          label="Adapter settings"
          full
          mono
          value={params.length ? <span className="ej-lines">{params.map((l) => <span key={l}>{l}</span>)}</span> : null}
        />
        <WorkspaceField label="Added" value={source.createdAt ? `${formatDateTime(source.createdAt)}${source.createdBy ? `, ${source.createdBy}` : ''}` : null} />
      </SectionCard>

      {scout && (
        <SectionCard title="Scout" icon={<TravelExploreOutlinedIcon />}>
          <WorkspaceField label="Outcome" value={scoutLine(source) ?? humanizeEnum(scout.outcome)} />
          <WorkspaceField label="Method" value={scout.method ? humanizeEnum(scout.method) : null} />
          <WorkspaceField label="Verified" value={yesNo(scout.verified)} />
          <WorkspaceField label="Site" mono value={scout.host} />
          <WorkspaceField label="Finished" value={scout.at || scout.finishedAt ? formatDateTime(scout.at || scout.finishedAt) : null} />
          <WorkspaceField label="AI spend" mono value={typeof scout.costUsd === 'number' && scout.costUsd > 0 ? usd(scout.costUsd) : null} />
          <WorkspaceField label="Error" full value={scout.error} />
          <WorkspaceField
            label="Notes"
            full
            value={scout.feedback?.length ? (
              <span className="ej-lines">
                {scout.feedback.filter(Boolean).map((l, i) => <span key={i}>{l}</span>)}
              </span>
            ) : null}
          />
        </SectionCard>
      )}
    </>
  );

  const jobs = (
    <SectionCard title="Fetched jobs" icon={<ListAltOutlinedIcon />} meta={jobsCount(fetchedTotal) ?? undefined}>
      <Box className="ej-ws-table">
        {inReview && (
          <Typography variant="body2" color="text.secondary">
            These jobs are hidden from candidates until the source is approved.
          </Typography>
        )}
        <SourceJobsTable
          sourceId={source.sourceId}
          canRun={canRun}
          onRun={() => actions.queueRun(actionable)}
          onTotal={setFetchedTotal}
        />
      </Box>
    </SectionCard>
  );

  const runsPanel = (
    <SectionCard title="Run history" icon={<HistoryIcon />} meta={runs.length ? `Latest ${runs.length}` : undefined}>
      <Box className="ej-ws-table">
        {runsFailed ? (
          <EmptyState compact title="Runs could not be loaded" body="Try again in a moment." />
        ) : runs.length === 0 ? (
          <EmptyState compact title="No runs yet" body="A run is recorded each time the source is fetched." />
        ) : (
          <MintlyTable
            pagination={{ enabled: false, page: 0, rowsPerPage: runs.length, totalNumberOfRows: runs.length, handleChangePage: () => {} }}
            rows={runs}
            getRowKey={(r: Run) => r.runId}
            selectors={['started', 'status', 'counts', 'requests']}
            columns={{
              started: { card: 'title', tablet: true },
              status: { card: 'status', tablet: true },
              counts: { card: 'fact', tablet: true },
              requests: { card: 'fact' },
            }}
            tableHeadRow={{ started: <>Started</>, status: <>Status</>, counts: <>Result</>, requests: <>Requests</> }}
            tableBodyRow={{
              started: (r: Run) => (
                <Box className="ej-source">
                  <span>{formatDateTime(r.startedAt, '')}</span>
                  <span className="ej-mono">
                    {humanizeEnum(r.trigger)}
                    {r.durationMs ? `, ${Math.round(r.durationMs / 1000)}s` : ''}
                  </span>
                </Box>
              ),
              status: (r: Run) => (
                <Box className="ej-source">
                  <CRMStatusChip status={r.status} />
                  {(r.errors ?? []).map((e) => runError(e)).filter(Boolean).map((text, i) => (
                    <Typography key={i} component="span" variant="body2" color="text.secondary" className="ej-wrap">
                      {text}
                    </Typography>
                  ))}
                </Box>
              ),
              counts: (r: Run) => <>{runCounts(r.counts)}</>,
              requests: (r: Run) => <>{requestTotal(r.requests).toLocaleString('en-IN')}</>,
            }}
          />
        )}
      </Box>
    </SectionCard>
  );

  const timeline: TimelineEntry[] = sourceEvents(source, runs).map((e) => ({
    id: e.id,
    kind: e.kind,
    text: e.text,
    meta: [formatDateTime(e.at), e.by].filter(Boolean).join(' · '),
  }));

  const tabContent: Record<string, React.ReactNode> = { overview, jobs, runs: runsPanel };

  return (
    <>
      <EntityWorkspace
        backLabel="External jobs"
        onBack={() => navigate(lastListView('/external-jobs'))}
        identityRail={identityRail}
        tabs={TABS}
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        stepper={decisionBar}
        activityRail={<ActivityRail timeline={timeline} />}
        activityOpen={activityOpen}
        onToggleActivity={toggleActivity}
      >
        {tabContent[activeTab]}
      </EntityWorkspace>
      {actions.dialog}
    </>
  );
};

export default SourceWorkspace;
